import React, { useState } from 'react'
import Icon from './Icons'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function StepContact({ contact, onChange, onNext, onBack, loading }) {
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  function handleChange(field, value) {
    onChange({ ...contact, [field]: value })
    if (touched[field] && errors[field]) {
      validateField(field, value)
    }
  }

  function handleBlur(field) {
    setTouched((t) => ({ ...t, [field]: true }))
    validateField(field, contact[field])
  }

  function validateField(field, value) {
    const msg = getFieldError(field, value)
    setErrors((e) => ({ ...e, [field]: msg }))
  }

  function getFieldError(field, value = '') {
    const v = value.trim()
    if (field === 'prenom' && !v)   return 'Le prénom est obligatoire.'
    if (field === 'nom'    && !v)   return 'Le nom est obligatoire.'
    if (field === 'societe' && !v)  return 'Le nom de la société est obligatoire.'
    if (field === 'email') {
      if (!v) return 'L\'adresse email est obligatoire.'
      if (!EMAIL_RE.test(v)) return 'Adresse email invalide.'
    }
    return null
  }

  function handleSubmit(e) {
    e.preventDefault()
    const fields = ['prenom', 'nom', 'societe', 'email']
    const newErrors = {}
    for (const f of fields) {
      const msg = getFieldError(f, contact[f])
      if (msg) newErrors[f] = msg
    }
    setTouched({ prenom: true, nom: true, societe: true, email: true })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    onNext()
  }

  return (
    <div className="step-enter max-w-lg mx-auto">
      {/* Header de l'étape */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-800">Vos coordonnées</h2>
        <p className="text-sm text-slate-500 mt-1">
          Un email de confirmation vous sera envoyé pour accéder à l'estimation.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Prénom + Nom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Prénom <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={contact.prenom}
              onChange={(e) => handleChange('prenom', e.target.value)}
              onBlur={() => handleBlur('prenom')}
              placeholder="Jean"
              autoComplete="given-name"
              className={`form-input ${errors.prenom ? 'error' : ''}`}
            />
            {errors.prenom && (
              <p className="text-xs text-red-500 mt-1">{errors.prenom}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Nom <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={contact.nom}
              onChange={(e) => handleChange('nom', e.target.value)}
              onBlur={() => handleBlur('nom')}
              placeholder="Dupont"
              autoComplete="family-name"
              className={`form-input ${errors.nom ? 'error' : ''}`}
            />
            {errors.nom && (
              <p className="text-xs text-red-500 mt-1">{errors.nom}</p>
            )}
          </div>
        </div>

        {/* Société */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Société <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={contact.societe}
            onChange={(e) => handleChange('societe', e.target.value)}
            onBlur={() => handleBlur('societe')}
            placeholder="Acme Industries"
            autoComplete="organization"
            className={`form-input ${errors.societe ? 'error' : ''}`}
          />
          {errors.societe && (
            <p className="text-xs text-red-500 mt-1">{errors.societe}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Adresse email professionnelle <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            value={contact.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            placeholder="jean.dupont@acme.fr"
            autoComplete="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1">{errors.email}</p>
          )}
        </div>

        {/* Mention RGPD */}
        <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-slate-50 border border-slate-200">
          <Icon name="info" className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-500 leading-relaxed">
            Vos données sont utilisées uniquement pour vous transmettre l'estimation et vous recontacter
            si besoin. Conformément au RGPD, vous pouvez demander leur suppression à tout moment à{' '}
            <span className="font-medium">contact@e2i-solutions.com</span>.
          </p>
        </div>

        {/* Boutons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="btn-secondary order-2 sm:order-1"
          >
            <Icon name="arrow-left" className="w-4 h-4" />
            Retour
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex-1 sm:flex-none sm:min-w-[200px] order-1 sm:order-2"
          >
            {loading ? (
              <>
                <LoadingSpinner />
                Envoi en cours…
              </>
            ) : (
              <>
                Recevoir mon estimation
                <Icon name="mail" className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

function LoadingSpinner() {
  return (
    <svg
      className="animate-spin w-4 h-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  )
}
